// MIT License - Copyright (c) 2026 Doug McNall
import { useState, useCallback, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAccessControl } from '../hooks/useAccessControl';
import LoadingSpinner from './LoadingSpinner';
import IconMap from './IconMap';
import TabAccessGuard from './TabAccessGuard';
import './TabManager.css';
import CommandPalette from './CommandPalette';

const Dashboard = lazy(() => import('../pages/Dashboard'));
const ProductsAdmin = lazy(() => import('../pages/ProductsAdmin'));
const CategoriesAdmin = lazy(() => import('../pages/CategoriesAdmin'));
const OrdersAdmin = lazy(() => import('../pages/OrdersAdmin'));
const OrderDetail = lazy(() => import('../pages/OrderDetail'));
const CustomersAdmin = lazy(() => import('../pages/CustomersAdmin'));
const CustomerDetail = lazy(() => import('../pages/CustomerDetail'));
const RecipesAdmin = lazy(() => import('../pages/RecipesAdmin'));
const ReviewsAdmin = lazy(() => import('../pages/ReviewsAdmin'));
const ShippingAdmin = lazy(() => import('../pages/ShippingAdmin'));
const PaymentGatewaysAdmin = lazy(() => import('../pages/PaymentGatewaysAdmin'));
const ManufacturingAdmin = lazy(() => import('../pages/ManufacturingAdmin'));
const BatchDetail = lazy(() => import('../pages/BatchDetail'));
const RevenueAdmin = lazy(() => import('../pages/RevenueAdmin'));
const UsersContainer = lazy(() => import('../pages/UsersContainer'));
const APITestPage = lazy(() => import('../pages/APITestPage'));
const SystemAdmin = lazy(() => import('../pages/SystemAdmin'));
const SettingsAdmin = lazy(() => import('../pages/SettingsAdmin'));
const CouponsAdmin = lazy(() => import('../pages/CouponsAdmin'));
const PromotionsAdmin = lazy(() => import('../pages/PromotionsAdmin'));
const SupportTickets = lazy(() => import('../pages/SupportTickets'));
const RecallLookup = lazy(() => import('../pages/RecallLookup'));
const MediaLibrary = lazy(() => import('../pages/MediaLibrary'));

// Sidebar group definitions — order matters
const SIDEBAR_GROUPS = [
  { id: 'overview',    label: 'Overview',           icon: '📊' },
  { id: 'business',    label: 'Sales & CRM',         icon: '🛒' },
  { id: 'catalog',     label: 'Catalog',             icon: '📦' },
  { id: 'content',     label: 'Content',             icon: '✍️'  },
  { id: 'operations',  label: 'Operations',          icon: '🏭' },
  { id: 'system',      label: 'System',              icon: '⚙️'  },
];

const TabManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { getAccessibleTabs, loading: accessLoading } = useAccessControl();

  const allPossibleTabs = useMemo(() => [
    { id: 'dashboard',        label: 'Dashboard',        path: '/admin-tool',                    component: Dashboard,           closeable: false, icon: IconMap.dashboard,                    category: 'overview'   },
    { id: 'orders',           label: 'Orders',           path: '/admin-tool/orders',             component: OrdersAdmin,         closeable: false, icon: IconMap.orders,                       category: 'business'   },
    { id: 'customers',        label: 'Customers',        path: '/admin-tool/customers',          component: CustomersAdmin,      closeable: false, icon: IconMap.customers,                    category: 'business'   },
    { id: 'support-tickets',  label: 'Support Tickets',  path: '/admin-tool/support-tickets',    component: SupportTickets,      closeable: false, icon: IconMap.customers,                    category: 'business'   },
    { id: 'products',         label: 'Products',         path: '/admin-tool/products',           component: ProductsAdmin,       closeable: false, icon: IconMap.products,                     category: 'catalog'    },
    { id: 'categories',       label: 'Categories',       path: '/admin-tool/categories',         component: CategoriesAdmin,     closeable: false, icon: IconMap.categories,                   category: 'catalog'    },
    { id: 'coupons',          label: 'Coupons',          path: '/admin-tool/coupons',            component: CouponsAdmin,        closeable: false, icon: IconMap.coupons || IconMap.products,  category: 'catalog'    },
    { id: 'promotions',       label: 'Promotions',       path: '/admin-tool/promotions',         component: PromotionsAdmin,     closeable: false, icon: IconMap.products,                     category: 'catalog'    },
    { id: 'media-library',    label: 'Media Library',    path: '/admin-tool/media',              component: MediaLibrary,        closeable: false, icon: IconMap.products,                     category: 'catalog'    },
    { id: 'recipes',          label: 'Recipes',          path: '/admin-tool/recipes',            component: RecipesAdmin,        closeable: false, icon: IconMap.recipes,                      category: 'content'    },
    { id: 'reviews',          label: 'Reviews',          path: '/admin-tool/reviews',            component: ReviewsAdmin,        closeable: false, icon: IconMap.reviews,                      category: 'content'    },
    { id: 'revenue',          label: 'Revenue',          path: '/admin-tool/revenue',            component: RevenueAdmin,        closeable: false, icon: IconMap.revenue,                      category: 'operations' },
    { id: 'manufacturing',    label: 'Manufacturing',    path: '/admin-tool/manufacturing',      component: ManufacturingAdmin,  closeable: false, icon: IconMap.manufacturing,                category: 'operations' },
    { id: 'shipping',         label: 'Shipping',         path: '/admin-tool/shipping',           component: ShippingAdmin,       closeable: false, icon: IconMap.shipping,                     category: 'operations' },
    { id: 'payment-gateways', label: 'Payment Gateways', path: '/admin-tool/payment-gateways',   component: PaymentGatewaysAdmin,closeable: false, icon: IconMap['payment-gateways'],          category: 'operations' },
    { id: 'recall-lookup',    label: 'Recall Lookup',    path: '/admin-tool/recall',             component: RecallLookup,        closeable: false, icon: IconMap.products,                     category: 'operations' },
    { id: 'users',            label: 'Users',            path: '/admin-tool/users',              component: UsersContainer,      closeable: false, icon: IconMap.users,                        category: 'system'     },
    { id: 'system',           label: 'System',           path: '/admin-tool/system',             component: SystemAdmin,         closeable: false, icon: IconMap.system,                       category: 'system'     },
    { id: 'settings',         label: 'Settings',         path: '/admin-tool/settings',           component: SettingsAdmin,       closeable: false, icon: IconMap.system,                       category: 'system'     },
    { id: 'api-test',         label: 'API Test',         path: '/admin-tool/api-test',           component: APITestPage,         closeable: false, icon: IconMap['api-test'],                  category: 'system'     },
  ], []);

  const [staticTabs, setStaticTabs] = useState([]);
  const [dynamicTabs, setDynamicTabs] = useState([]);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Sidebar state — collapsed groups + sidebar collapsed
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin-sidebar-collapsed-groups') || '{}'); }
    catch { return {}; }
  });
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('admin-sidebar-collapsed') === 'true';
  });

  const getCurrentTabId = useCallback(() => {
    const path = location.pathname;
    const customerMatch = path.match(/\/admin-tool\/customers\/(.+)/);
    if (customerMatch) return `customer-${customerMatch[1]}`;
    const orderMatch = path.match(/\/admin-tool\/orders\/(.+)/);
    if (orderMatch) return `order-${orderMatch[1]}`;
    const batchMatch = path.match(/\/admin-tool\/batches\/(.+)/);
    if (batchMatch) return `batch-${batchMatch[1]}`;
    const matched = allPossibleTabs.find(t => t.path === path || (t.path !== '/admin-tool' && path.startsWith(t.path)));
    return matched?.id || 'dashboard';
  }, [location.pathname, allPossibleTabs]);

  const [activeTabId, setActiveTabId] = useState('dashboard');

  useEffect(() => {
    if (!accessLoading) {
      const accessibleTabIds = getAccessibleTabs();
      setStaticTabs(allPossibleTabs.filter(t => accessibleTabIds.includes(t.id)));
    }
  }, [accessLoading, getAccessibleTabs, allPossibleTabs]);

  useEffect(() => {
    if (staticTabs.length > 0) setActiveTabId(getCurrentTabId());
  }, [staticTabs, getCurrentTabId]);

  useEffect(() => {
    setActiveTabId(getCurrentTabId());
  }, [location.pathname, getCurrentTabId]);

  const openCustomerDetailTab = useCallback((customerId, customerName) => {
    const tabId = `customer-${customerId}`;
    if (!dynamicTabs.find(t => t.id === tabId)) {
      setDynamicTabs(prev => [...prev, { id: tabId, label: customerName || `Customer ${customerId}`, path: `/admin-tool/customers/${customerId}`, component: CustomerDetail, closeable: true, icon: IconMap.customers, category: 'dynamic', customerId }]);
    }
    setActiveTabId(tabId);
    navigate(`/admin-tool/customers/${customerId}`);
  }, [dynamicTabs, navigate]);

  const openOrderDetailTab = useCallback((orderId, orderNumber) => {
    const tabId = `order-${orderId}`;
    if (!dynamicTabs.find(t => t.id === tabId)) {
      setDynamicTabs(prev => [...prev, { id: tabId, label: orderNumber || `Order ${orderId}`, path: `/admin-tool/orders/${orderId}`, component: OrderDetail, closeable: true, icon: IconMap.orders, category: 'dynamic', orderId }]);
    }
    setActiveTabId(tabId);
    navigate(`/admin-tool/orders/${orderId}`);
  }, [dynamicTabs, navigate]);

  const openBatchDetailTab = useCallback((batchId, batchNumber) => {
    const tabId = `batch-${batchId}`;
    if (!dynamicTabs.find(t => t.id === tabId)) {
      setDynamicTabs(prev => [...prev, { id: tabId, label: batchNumber || `Batch ${batchId}`, path: `/admin-tool/batches/${batchId}`, component: BatchDetail, closeable: true, icon: IconMap.manufacturing, category: 'dynamic', batchId }]);
    }
    setActiveTabId(tabId);
    navigate(`/admin-tool/batches/${batchId}`);
  }, [dynamicTabs, navigate]);

  const closeTab = useCallback((tabId) => {
    setDynamicTabs(prev => prev.filter(t => t.id !== tabId));
    if (activeTabId === tabId) {
      setActiveTabId('dashboard');
      navigate('/admin-tool');
    }
  }, [activeTabId, navigate]);

  const switchTab = useCallback((tabId) => {
    const allTabs = [...staticTabs, ...dynamicTabs];
    const tab = allTabs.find(t => t.id === tabId);
    if (tab) { setActiveTabId(tabId); navigate(tab.path); }
  }, [staticTabs, dynamicTabs, navigate]);

  const refreshCurrentTab = useCallback(() => setRefreshCounter(c => c + 1), []);

  const toggleGroup = useCallback((groupId) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [groupId]: !prev[groupId] };
      localStorage.setItem('admin-sidebar-collapsed-groups', JSON.stringify(next));
      return next;
    });
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      localStorage.setItem('admin-sidebar-collapsed', String(!prev));
      return !prev;
    });
  }, []);

  // Cmd+K / Ctrl+K to open command palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const allTabs = [...staticTabs, ...dynamicTabs];
  const activeTab = allTabs.find(t => t.id === activeTabId) || staticTabs[0];
  const ActiveComponent = activeTab?.component;

  const customerId = activeTab?.customerId;
  const orderId = activeTab?.orderId;
  const batchId = activeTab?.batchId;

  // Group static tabs by category
  const groupedTabs = useMemo(() => {
    const groups = {};
    SIDEBAR_GROUPS.forEach(g => { groups[g.id] = []; });
    staticTabs.forEach(tab => {
      if (groups[tab.category]) groups[tab.category].push(tab);
    });
    return groups;
  }, [staticTabs]);

  return (
    <div className={`tab-manager ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <nav className="sidebar">
        <div className="sidebar-header">
          {!sidebarCollapsed && (
            <button className="sidebar-search-btn" onClick={() => setShowCommandPalette(true)} title="Search (⌘K)">
              <span>🔍</span> <span className="sidebar-search-label">Search</span> <kbd>⌘K</kbd>
            </button>
          )}
          {sidebarCollapsed && (
            <button className="sidebar-search-icon-btn" onClick={() => setShowCommandPalette(true)} title="Search (⌘K)">🔍</button>
          )}
          <button className="sidebar-toggle" onClick={toggleSidebar} title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {sidebarCollapsed ? '›' : '‹'}
          </button>
        </div>

        <div className="sidebar-content">
          {/* Dynamic (open detail) tabs */}
          {dynamicTabs.length > 0 && (
            <div className="sidebar-group">
              {!sidebarCollapsed && <div className="sidebar-group-header">Open</div>}
              {dynamicTabs.map(tab => (
                <div
                  key={tab.id}
                  className={`sidebar-item ${activeTabId === tab.id ? 'active' : ''}`}
                  onClick={() => switchTab(tab.id)}
                  title={sidebarCollapsed ? tab.label : undefined}
                >
                  <span className="sidebar-item-icon">{tab.icon ? <tab.icon size={16} /> : '📄'}</span>
                  {!sidebarCollapsed && (
                    <>
                      <span className="sidebar-item-label">{tab.label}</span>
                      <button className="sidebar-item-close" onClick={e => { e.stopPropagation(); closeTab(tab.id); }} title="Close">×</button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Static grouped tabs */}
          {SIDEBAR_GROUPS.map(group => {
            const tabs = groupedTabs[group.id] || [];
            if (tabs.length === 0) return null;
            const isCollapsed = collapsedGroups[group.id];
            return (
              <div key={group.id} className="sidebar-group">
                <button
                  className="sidebar-group-header"
                  onClick={() => !sidebarCollapsed && toggleGroup(group.id)}
                  title={sidebarCollapsed ? group.label : undefined}
                >
                  <span className="sidebar-group-icon">{group.icon}</span>
                  {!sidebarCollapsed && (
                    <>
                      <span className="sidebar-group-label">{group.label}</span>
                      <span className={`sidebar-group-chevron ${isCollapsed ? 'collapsed' : ''}`}>›</span>
                    </>
                  )}
                </button>
                {(!isCollapsed || sidebarCollapsed) && tabs.map(tab => (
                  <div
                    key={tab.id}
                    className={`sidebar-item ${activeTabId === tab.id ? 'active' : ''}`}
                    onClick={() => switchTab(tab.id)}
                    title={sidebarCollapsed ? tab.label : undefined}
                  >
                    <span className="sidebar-item-icon">{tab.icon ? <tab.icon size={16} /> : '•'}</span>
                    {!sidebarCollapsed && <span className="sidebar-item-label">{tab.label}</span>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Refresh button at bottom */}
        {!sidebarCollapsed && (
          <div className="sidebar-footer">
            <button className="sidebar-refresh" onClick={refreshCurrentTab} title="Refresh current page">↻ Refresh</button>
          </div>
        )}
      </nav>

      {/* Main content */}
      <div className="tab-content">
        {ActiveComponent && (
          <TabAccessGuard tabId={activeTab?.id}>
            <Suspense fallback={<LoadingSpinner />}>
              <ActiveComponent
                key={`${activeTab?.id}-${refreshCounter}`}
                onOpenCustomerDetail={openCustomerDetailTab}
                onOpenOrderDetail={openOrderDetailTab}
                onOpenBatchDetail={openBatchDetailTab}
                customerId={customerId}
                orderId={orderId}
                batchId={batchId}
              />
            </Suspense>
          </TabAccessGuard>
        )}
      </div>
      {showCommandPalette && (
        <CommandPalette
          tabs={staticTabs}
          onNavigate={switchTab}
          onOpenCustomerDetail={openCustomerDetailTab}
          onOpenOrderDetail={openOrderDetailTab}
          onClose={() => setShowCommandPalette(false)}
        />
      )}
    </div>
  );
};

export default TabManager;
